import { useEffect, useState } from "react";
import { Link, useRouter } from "@tanstack/react-router";
import {
  useAuthProvidersQuery,
  useLoginMutation,
  useRegisterMutation,
} from "@/api/auth";
import { apiUrl } from "@/api/client";

export function LoginPage() {
  const [isRegister, setIsRegister] = useState(false);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [oauthError, setOauthError] = useState<string | null>(null);
  const router = useRouter();

  const loginMutation = useLoginMutation();
  const registerMutation = useRegisterMutation();
  const providersQuery = useAuthProvidersQuery();

  const isLoading = loginMutation.isPending || registerMutation.isPending;
  const error = loginMutation.error ?? registerMutation.error;

  // Read URL params on mount: ?invite_code=xxx&register=1&oauth_error=...
  // The invite email sends users here with these params pre-set.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Auto-switch to register mode if ?register=1 is present
    if (params.get("register") === "1") {
      setIsRegister(true);
    }

    // Pre-fill invite code from URL (from email link)
    const code = params.get("invite_code");
    if (code) {
      setInviteCode(code);
      // Also switch to register since invite codes are for signup
      setIsRegister(true);
    }

    // Surface ?oauth_error=... if the backend bounced us back here.
    const err = params.get("oauth_error");
    if (err) {
      setOauthError(err);
    }

    // Clean up URL params so they don't persist on refresh
    if (code || err || params.get("register")) {
      params.delete("oauth_error");
      params.delete("invite_code");
      params.delete("register");
      const search = params.toString();
      window.history.replaceState(
        null,
        "",
        `/login${search ? `?${search}` : ""}`,
      );
    }
  }, []);

  function handleGoogleSignIn() {
    // Pass invite code through the OAuth flow so the backend can consume
    // it after Google redirects back. The backend encodes it in the OAuth
    // state parameter which Google round-trips.
    const code = inviteCode.trim();
    const url = code
      ? apiUrl(`/auth/google/start?invite_code=${encodeURIComponent(code)}`)
      : apiUrl("/auth/google/start");
    window.location.href = url;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const onSuccess = () => void router.navigate({ to: "/" });

    const codePayload = inviteCode.trim() ? { invite_code: inviteCode.trim() } : {};

    if (isRegister) {
      registerMutation.mutate(
        { username, email, password, ...codePayload },
        { onSuccess },
      );
    } else {
      loginMutation.mutate(
        { username, password, ...codePayload },
        { onSuccess },
      );
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface">
      <div className="w-full max-w-md p-6">
        {/* Brand */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-semibold tracking-tight text-ink">
            MotionForge
          </h1>
          <p className="mt-2 text-sm text-ink-3">
            Motion graphics, forged by AI
          </p>
        </div>

        {/* Invite banner — shown when an invite code is pre-filled from the URL */}
        {inviteCode && (
          <div className="mb-4 rounded-xl border border-accent/30 bg-accent-soft px-4 py-3">
            <div className="text-sm font-medium text-accent">
              You've been invited to MotionForge
            </div>
            <div className="mt-1 text-xs text-ink-2">
              {isRegister
                ? "Create an account to accept the invite, or sign in if you already have one."
                : "Sign in to accept the invite, or create a new account."}
            </div>
          </div>
        )}

        <div className="rounded-2xl border border-line bg-surface-2 p-6">
          <h2 className="mb-6 text-lg font-semibold text-ink">
            {isRegister ? "Create Account" : "Sign In"}
            {inviteCode && (
              <span className="ml-2 text-sm font-normal text-accent">+ Accept Invite</span>
            )}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-ink-2">
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoFocus
                className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none"
              />
            </div>

            {isRegister && (
              <div>
                <label className="mb-1 block text-sm font-medium text-ink-2">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none"
                />
              </div>
            )}

            <div>
              <label className="mb-1 flex items-center justify-between">
                <span className="block text-sm font-medium text-ink-2">
                  Password
                </span>
                {!isRegister && (
                  <Link
                    to="/forgot-password"
                    className="text-xs text-ink-3 hover:text-ink"
                  >
                    Forgot?
                  </Link>
                )}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none"
              />
            </div>

            {/* Show invite code field when:
                - Registering (always, required or optional)
                - Logging in AND a code is pre-filled from the URL */}
            {(isRegister || inviteCode) && (
              <div>
                <label className="mb-1 block text-sm font-medium text-ink-2">
                  Invite Code
                  {isRegister && providersQuery.data?.require_invite && (
                    <span className="ml-1 text-danger">*</span>
                  )}
                </label>
                <input
                  type="text"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  required={isRegister && providersQuery.data?.require_invite}
                  placeholder={
                    isRegister
                      ? providersQuery.data?.require_invite
                        ? "Required — ask an admin for a code"
                        : "Optional — paste if you have one"
                      : "Team invite code"
                  }
                  className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none"
                />
              </div>
            )}

            {error && (
              <div className="rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-xs text-danger">
                {error.message}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full rounded-lg bg-accent py-2 text-sm font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:opacity-40"
            >
              {isLoading
                ? "Loading..."
                : isRegister
                  ? "Create Account"
                  : "Sign In"}
            </button>
          </form>

          {providersQuery.data?.google && (
            <>
              <div className="my-4 flex items-center gap-3">
                <div className="h-px flex-1 bg-line" />
                <span className="text-xs text-ink-3">or</span>
                <div className="h-px flex-1 bg-line" />
              </div>

              <button
                type="button"
                onClick={handleGoogleSignIn}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-line py-2 text-sm font-medium text-ink-2 transition-colors hover:bg-surface-3 hover:text-ink"
              >
                <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                  <path fill="#4CAF50" d="M24 44c5.5 0 10.4-2.1 14.1-5.5l-6.5-5.5C29.6 34.6 26.9 35.5 24 35.5c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
                  <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.7l6.5 5.5C41.4 35.4 44 30.1 44 24c0-1.3-.1-2.4-.4-3.5z"/>
                </svg>
                Continue with Google
              </button>

              {oauthError && (
                <div className="mt-3 rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-xs text-danger">
                  Google sign-in failed: {oauthError}
                </div>
              )}
            </>
          )}

          <div className="mt-4 text-center">
            <button
              onClick={() => setIsRegister(!isRegister)}
              className="text-xs text-ink-2 hover:text-ink"
            >
              {isRegister
                ? "Already have an account? Sign in"
                : "Need an account? Register"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
